import * as React from 'react';
import { IconButton, styled, Theme } from '@mui/material';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';

import { faPaperclip, faTrash } from '@fortawesome/free-solid-svg-icons';

import { CerebroHeading, CerebroIcon, CerebroPrimaryButton, CerebroText } from '.';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SxProps } from '@mui/system';
import { useSnackbar } from 'notistack';

export const CerebroFileDragAndDrop: React.FC<CerebroFileDragAndDropProps> = ({
	name,
	value,
	onChange,
	sx,
	multiple,
	onlyPdf,
}) => {
	const snackbar = useSnackbar();
	const zipText = 'Files dragged from inside zip files will not successfully upload. Be sure to unzip the file and upload the uncompressed file.';

	const onDrop = (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
		const hasFileLengthError = acceptedFiles.filter(file => file.name.length > 150).length;
		const isZipFile = acceptedFiles.filter(file => file.size == 0).length;
		const hasSizeError = acceptedFiles.filter(file => file.size > 50 * 1024 * 1024).length;

		const allFiles = [
			...value,
			...acceptedFiles.filter(
				acceptedFile => !value.some(file => file.name === acceptedFile.name) && acceptedFile.name.length <= 150 && acceptedFile.size > 0 && acceptedFile.size <= (50 * 1024 * 1024)
			),
		];

		if (hasFileLengthError) {
			snackbar.enqueueSnackbar('Document file names cannot exceed 150 characters.', {
				variant: 'error',
			});
		}

		if (hasSizeError) {
			snackbar.enqueueSnackbar('Only PDF with maximum size of 50 MB is allowed.', {
				variant: 'error',
			});
		}

		if (isZipFile) {
			snackbar.enqueueSnackbar('Zip file not allowed.', {
				variant: 'error',
			});
		}

		onChange({
			target: {
				name,
				value: allFiles,
			},
		});
	};

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		multiple: multiple,
		accept: onlyPdf
			? ['application/pdf']
			: [
				'application/pdf',
				'application/msword',
				'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
				'application/vnd.ms-excel',
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				'text/csv',
				'application/vnd.openxmlformats-officedocument.presentationml.presentation',
				'application/vnd.ms-powerpoint',
			],
			maxSize: (50 * 1024 * 1024)
	});

	const { onClick, ...rootProps } = getRootProps();
	const inputProps = getInputProps();

	const onDelete = (file: File) => () => {
		const filesWithout = value.filter(x => x !== file);

		onChange({
			target: {
				name,
				value: filesWithout,
			},
		});
	};

	return (
		<>
			<StyledDndWrapper {...rootProps} sx={sx} isDragActive={isDragActive}>
				<input {...inputProps} />
				<CerebroHeading size="h2">Drag and Drop Your {multiple ? 'Documents' : 'Document'} Here</CerebroHeading>
				<CerebroText sx={{ margin: 2 }}>or</CerebroText>
				<CerebroPrimaryButton wide onClick={onClick}>
					Browse Files
				</CerebroPrimaryButton>
				<CerebroText variant="hint" sx={{ marginTop: 4.5 }}>
					{onlyPdf
						? `Only PDF with maximum size of 50 MB is allowed. ${zipText}`
						: `Only DOC, DOCX, PDF, XLS, XLSX, PPT, PPTX, or CSV with maximum size of 50 MB is allowed. ${zipText}`}
				</CerebroText>
			</StyledDndWrapper>
			{value.map(file => (
				<CerebroUploadFile key={file.name} file={file} onDelete={onDelete(file)} />
			))}
		</>
	);
};

interface CerebroFileDragAndDropProps {
	name: string;
	value: File[];

	onlyPdf: boolean;
	multiple: boolean;
	sx?: SxProps<Theme>;
	onChange: (event: CerebroFileDragAndDropChangeEvent) => void;
}

interface CerebroFileDragAndDropChangeEvent {
	target: {
		name: string;
		value: File[];
	};
}

type DnDWrapperProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
	isDragActive: boolean;
};

// eslint-disable-next-line react/display-name
const DnDWrapper = React.forwardRef((props: DnDWrapperProps, ref: React.LegacyRef<HTMLDivElement> | undefined) => (
	<div ref={ref} {...props} />
));

const StyledDndWrapper = styled(DnDWrapper, { shouldForwardProp: prop => prop !== 'isDragActive' })(
	({ isDragActive, theme }) => ({
		backgroundColor: theme.palette.common.white,
		borderWidth: 1,
		borderStyle: 'solid',
		borderColor: isDragActive ? theme.palette.primary.main : theme.palette.gray.medium,
		borderRadius: theme.shape.borderRadius,

		boxShadow: isDragActive ? theme.solidShadow(theme.palette.primary.main) : theme.shadows[3],

		alignItems: 'center',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',

		padding: theme.spacing(3),
		paddingTop: theme.spacing(4.5),
	})
);

const CerebroUploadFile: React.FC<CerebroUploadFileProps> = ({ file, onDelete }) => {
	return (
		<StyledFileWrapper>
			<div>
				<CerebroIcon padRight sx={theme => ({ color: theme.palette.primary.main })}>
					<FontAwesomeIcon icon={faPaperclip} size="lg" />
				</CerebroIcon>{' '}
				{file.name}
			</div>
			<IconButton sx={theme => ({ color: theme.palette.gray.dark })} onClick={onDelete}>
				<FontAwesomeIcon icon={faTrash} size="sm" />
			</IconButton>
		</StyledFileWrapper>
	);
};

interface CerebroUploadFileProps {
	file: File;
	onDelete: () => void;
}

const StyledFileWrapper = styled('div')(({ theme }) => ({
	backgroundColor: theme.palette.common.white,
	borderWidth: 1,
	borderStyle: 'solid',
	borderColor: theme.palette.gray.medium,
	borderRadius: theme.shape.borderRadius,
	boxShadow: theme.shadows[3],

	alignItems: 'center',
	display: 'flex',
	justifyContent: 'space-between',

	padding: theme.spacing(1.5),
	margin: theme.spacing(2, 0),
}));
